/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {useCallback, useEffect, useRef, useState} from 'react';
// @ts-ignore
import {VC} from 'vidyoclient-nativewebrtc-sdk';

import {
    VIDEO_CUSTOM_BG_BASE64_SRC,
    VIDEO_DEFAULT_CAMERA_ID,
    VIDEO_DEFAULT_CAMERA_TITLE,
    VIDEO_DEFAULT_MIC_ID,
    VIDEO_DEFAULT_MIC_TITLE,
    VIDEO_DEFAULT_SPEAKER_ID,
    VIDEO_DEFAULT_SPEAKER_TITLE,
    VIDEO_FILTER_OPTION,
    VIDEO_IS_CUSTOM_BG_SELECTED,
} from '@/constants/constants';
import {VideoFilterOptions} from '@/types/video';
import {vidyoProviderLogs} from '@/utils/general';

import {registerCamera} from '../registerCamera';
import {registerMicrophone} from '../registerMicrophone';
import {registerSpeaker} from '../registerSpeaker';
import {CallEndReason, SelectOption, VidyoSdkCallEndReason} from '../types';
import {registerFilter, unregisterFilter} from '../utils';
import {defaultBgImage} from '../utils/defaultBgImage';
import {loadPrevSelectedDevice} from '../utils/loadPrevSelectedDevice';

export type UseVidyoConnectionOptions = {
    currentCallMeetingId: string;
    user: {
        firstName: string;
        id: number;
        lastName?: string;
    };
    isForceLoadingState?: boolean;
    initCameraList?: SelectOption[];
    onClose?: () => void;
    onDisconnect?: () => void;
    /** Called when bg noise cancellation is initialised on connect */
    onBgNoiseInit: (enabled: boolean) => void;
    isDebug: boolean;
    setDebugStatus: React.Dispatch<React.SetStateAction<string[] | null>>;
    callEndReason?: CallEndReason;
};

type OnCloseProps = {
    timeout?: number;
    forcePreventFromClose?: boolean;
};

const DEF_CLOSE_TIMEOUT = 2000;

export const useVidyoConnection = ({
    currentCallMeetingId,
    user,
    isForceLoadingState = false,
    initCameraList = [],
    onClose,
    onDisconnect,
    onBgNoiseInit,
    isDebug,
    setDebugStatus,
}: UseVidyoConnectionOptions) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isConnected, setIsConnected] = useState(false);

    const [curMic, setCurMic] = useState<SelectOption | null>(null);
    const [curSpeaker, setCurSpeaker] = useState<SelectOption | null>(null);
    const [curCamera, setCurCamera] = useState<SelectOption | null>(null);
    const [cameraList, setCameraList] = useState<SelectOption[]>(initCameraList);
    const [micList, setMicList] = useState<SelectOption[]>([]);
    const [speakerList, setSpeakerList] = useState<SelectOption[]>([]);
    const [callEndReason, setCallEndReason] = useState<CallEndReason>(CallEndReason.Default);
    const connectionInitiatedRef = useRef(false);

    const handleDisconnect = useCallback(() => {
        onDisconnect?.();
    }, [onDisconnect]);

    const handleClose = useCallback(
        (props?: OnCloseProps) => {
            onDisconnect?.();
            if (!isDebug && !props?.forcePreventFromClose) {
                setTimeout(() => {
                    onClose?.();
                }, props?.timeout || DEF_CLOSE_TIMEOUT);
            }
        },
        [isDebug, onClose, onDisconnect],
    );

    const handleDelayedIdle = useCallback(() => {
        setTimeout(() => {
            setIsLoading(false);
        }, 100);
    }, []);

    useEffect(() => {
        const connectionStatus = document.getElementById('connectionStatus');
        if (isForceLoadingState) {
            connectionStatus?.classList.add('loading');
        }
    }, [isForceLoadingState]);

    useEffect(() => {
        const viewElement = document.getElementById('renderer');
        if (connectionInitiatedRef.current || isConnected || isLoading || !viewElement) {
            return;
        }

        connectionInitiatedRef.current = true;

        setIsLoading(true);

        const connectLogMessage = `Connected to room ${currentCallMeetingId} as ${user?.firstName} pid${user?.id}, host: ${window.VIDYO_TENANT_URL}`;

        let preventFromClose = false;

        VC.CreateVidyoConnector({
            viewId: 'renderer',
            viewStyle: 'VIDYO_CONNECTORVIEWSTYLE_Default',
            remoteParticipants: 8,
            logFileFilter: 'error@VidyoClient',
            logFileName: '',
            userData: 0,
            constraints: {},
        })
            .then((vc: any) => {
                window['vidyoConnector'] = vc;

                vc.ConnectToRoomAsGuest({
                    host: `${window.VIDYO_TENANT_URL}`,
                    roomKey: currentCallMeetingId,
                    roomPin: '1',
                    displayName: `${user?.firstName} pid${user?.id}`,
                    onSuccess: () => {
                        vidyoProviderLogs({message: connectLogMessage, eventType: 'connection', status: 'success'});
                        handleDelayedIdle();
                        setIsConnected(true);
                    },
                    onFailure: (reason: VidyoSdkCallEndReason) => {
                        if (reason === VidyoSdkCallEndReason.InvalidResourceId) {
                            preventFromClose = true;
                            setCallEndReason(CallEndReason.ExpiredLink);
                        }

                        vidyoProviderLogs({
                            message: 'Could not connect to room',
                            eventType: 'connection',
                            status: 'error',
                            error: reason,
                        });

                        if (isDebug) {
                            setDebugStatus((prev) => [...prev, `onFailure: ${JSON.stringify(reason)}`]);
                        }

                        handleDelayedIdle();
                        setIsConnected(false);
                        handleDisconnect();
                    },
                    onDisconnected: (reason: VidyoSdkCallEndReason) => {
                        if (reason === VidyoSdkCallEndReason.Booted) {
                            preventFromClose = true;
                            setCallEndReason(CallEndReason.NoParticipants);
                        }
                        vidyoProviderLogs({
                            message: `Disconnected from room ${currentCallMeetingId} with reason: ${reason}`,
                            eventType: 'connection',
                            status: 'info',
                        });
                        window?.vidyoConnector.SelectLocalWindowShare({localWindowShare: null});
                        window?.vidyoConnector.ShowWindowSharePreview({preview: false});
                        handleDelayedIdle();
                        setIsConnected(false);
                        if (isDebug) {
                            setDebugStatus((prev) => [...prev, `onDisconnected: ${JSON.stringify(reason)}`]);
                        } else {
                            handleClose({
                                forcePreventFromClose: preventFromClose,
                            });
                        }
                    },
                });
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return vc;
            })
            .then((vc: any) => {
                vc.RegisterLocalWindowShareEventListener({
                    onAdded: (localWindowShare: any) => {
                        window.localWindowShare = localWindowShare;
                    },
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    onRemoved: () => {},
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    onSelected: () => {},
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    onStateUpdated: () => {},
                });

                registerCamera({
                    onAdded: (camera) => {
                        setCameraList((prev) => [...prev, {value: camera.id, label: camera.name}]);
                    },
                    onRemoved: (cameraId) => {
                        setCameraList((prev) => {
                            const newList = prev.filter((c) => c.value !== cameraId);
                            if (curCamera?.value === cameraId) setCurCamera(newList[0] || null);
                            return newList;
                        });
                    },
                    setCurCamera: (camera) => setCurCamera({value: camera.id, label: camera.name}),
                });

                registerSpeaker({
                    onAdded: (speaker) => {
                        setSpeakerList((prev) => [...prev, {value: speaker.id, label: speaker.name}]);
                    },
                    onRemoved: (speakerId) => {
                        setSpeakerList((prev) => {
                            const newList = prev.filter((s) => s.value !== speakerId);
                            if (curSpeaker?.value === speakerId) setCurSpeaker(newList[0] || null);
                            return newList;
                        });
                    },
                    setCurSpeaker: (speaker) => setCurSpeaker({value: speaker.id, label: speaker.name}),
                });

                registerMicrophone({
                    onAdded: (mic) => {
                        setMicList((prev) => [...prev, {value: mic.id, label: mic.name}]);
                    },
                    onRemoved: (micId) => {
                        setMicList((prev) => {
                            const newList = prev.filter((m) => m.value !== micId);
                            if (curMic?.value === micId) setCurMic(newList[0] || null);
                            return newList;
                        });
                    },
                    setCurMic: (mic) => setCurMic({value: mic.id, label: mic.name}),
                });

                loadPrevSelectedDevice('camera');
                loadPrevSelectedDevice('microphone');
                loadPrevSelectedDevice('speaker');

                const savedCustomBg = localStorage.getItem(VIDEO_CUSTOM_BG_BASE64_SRC);
                const savedFilterOption = localStorage.getItem(VIDEO_FILTER_OPTION) as VideoFilterOptions;
                const savedIsCustomBgSelected = localStorage.getItem(VIDEO_IS_CUSTOM_BG_SELECTED);

                unregisterFilter();

                // Initialise background noise cancellation by default
                setTimeout(() => {
                    onBgNoiseInit(true);
                    registerFilter('bg_noise_cancellation').catch((err) => {
                        vidyoProviderLogs({
                            message: 'Could not enable BACKGROUND NOISE CANCELLATION',
                            eventType: 'stream-effect',
                            status: 'error',
                            error: err,
                        });
                    });
                }, 0);

                if (savedFilterOption === 'blur') {
                    setTimeout(() => {
                        registerFilter(savedFilterOption).catch((err) => {
                            vidyoProviderLogs({
                                message: 'Could not enable BLUR EFFECT',
                                eventType: 'stream-effect',
                                status: 'error',
                                error: err,
                            });
                        });
                    }, 0);
                }

                if (savedFilterOption === 'bg_image') {
                    setTimeout(() => {
                        registerFilter(
                            'bg_image',
                            savedIsCustomBgSelected && savedCustomBg ? savedCustomBg : defaultBgImage,
                        ).catch((err) => {
                            vidyoProviderLogs({
                                message: 'Could not enable BACKGROUND IMAGE EFFECT',
                                eventType: 'stream-effect',
                                status: 'error',
                                error: err,
                            });
                        });
                    }, 0);
                }
            })
            .catch((err: any) => {
                vidyoProviderLogs({
                    message: 'An error occurred',
                    eventType: 'connection',
                    status: 'error',
                    error: err,
                });
                localStorage.setItem('VIDYO_ERROR', JSON.stringify(err));
                if (isDebug) {
                    setDebugStatus((prev) => [...prev, `CreateVidyoConnector failed: ${JSON.stringify(err)}`]);
                }
                handleDisconnect();
                handleDelayedIdle();
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMicChange = async (mic: any) => {
        const micValue = mic?.value;
        const curMicObj = window?.['microphone']?.find((microphone: any) => microphone.id === micValue);
        vidyoProviderLogs({
            message: `[VideoWidget] Microphone changed manually to: ID ${micValue} | Name ${curMicObj.name}`,
            eventType: 'device',
        });
        setCurMic(mic);
        localStorage.setItem(VIDEO_DEFAULT_MIC_ID, micValue);
        localStorage.setItem(VIDEO_DEFAULT_MIC_TITLE, curMicObj.name);
        try {
            await window.vidyoConnector.SelectLocalMicrophone({localMicrophone: curMicObj});
        } catch (error) {
            vidyoProviderLogs({message: 'Error selecting microphone', eventType: 'device', status: 'error', error});
        }
    };

    const handleCameraChange = async (cam: any) => {
        const camValue = cam?.value;
        const curCamObj = window?.['camera']?.find((camera: any) => camera.id === camValue);
        vidyoProviderLogs({
            message: `[VideoWidget] Camera changed manually to: ID ${camValue} | Name ${curCamObj.name}`,
            eventType: 'device',
        });
        setCurCamera(cam);
        localStorage.setItem(VIDEO_DEFAULT_CAMERA_ID, camValue);
        localStorage.setItem(VIDEO_DEFAULT_CAMERA_TITLE, curCamObj.name);
        try {
            await window.vidyoConnector.SelectLocalCamera({localCamera: curCamObj});
        } catch (error) {
            vidyoProviderLogs({message: 'Error selecting camera', eventType: 'device', status: 'error', error});
        }
    };

    const handleSpeakerChange = async (speaker: any) => {
        const speakerValue = speaker?.value;
        const curSpeakerObj = window?.['speaker']?.find((spk: any) => spk.id === speakerValue);
        vidyoProviderLogs({
            message: `[VideoWidget] Speaker changed manually to: ID ${speakerValue} | Name ${curSpeakerObj.name}`,
            eventType: 'device',
        });
        setCurSpeaker(speaker);
        localStorage.setItem(VIDEO_DEFAULT_SPEAKER_ID, speakerValue);
        localStorage.setItem(VIDEO_DEFAULT_SPEAKER_TITLE, curSpeakerObj.name);
        try {
            await window.vidyoConnector.SelectLocalSpeaker({localSpeaker: curSpeakerObj});
        } catch (error) {
            vidyoProviderLogs({message: 'Error selecting speaker', eventType: 'device', status: 'error', error});
        }
    };

    return {
        isLoading,
        isConnected,
        cameraList,
        micList,
        speakerList,
        curCamera,
        curMic,
        curSpeaker,
        handleClose,
        handleMicChange,
        handleCameraChange,
        handleSpeakerChange,
        callEndReason,
    };
};
